import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, Loader2, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { serviceOptions, dogSizes, dogCharacters, serviceZones } from '../data/mock';
import { trackContactFormSubmit, trackWhatsAppClick, getStoredUtm } from '../lib/analytics';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [whatsappLink, setWhatsappLink] = useState('');
  const [hasMedication, setHasMedication] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  // Honeypot: campo oculto que humanos no rellenan. Si llega con valor, backend lo descarta.
  const [website, setWebsite] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    zona: '',
    servicio: '',
    fechaInicio: '',
    fechaFin: '',
    tamano: '',
    caracter: '',
    medicacion: '',
    observaciones: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const formatDateSpanish = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const generateWhatsAppMessage = () => {
    const fechaInicioFormatted = formatDateSpanish(formData.fechaInicio);
    const fechaFinFormatted = formatDateSpanish(formData.fechaFin);
    
    const fechas = fechaFinFormatted 
      ? `del ${fechaInicioFormatted} al ${fechaFinFormatted}`
      : `el ${fechaInicioFormatted}`;
    
    const medicacionText = hasMedication && formData.medicacion 
      ? formData.medicacion 
      : 'No necesita';
    
    const observacionesText = formData.observaciones || 'Ninguna';
    
    const message = `Hola Fabio, soy ${formData.nombre}.

Me gustaría reservar el servicio de ${formData.servicio} en ${formData.zona} ${fechas}.

🐶 Datos de mi perro:
• Tamaño: ${formData.tamano}
• Carácter: ${formData.caracter}
• Medicación: ${medicacionText}
• Observaciones: ${observacionesText}

¿Tienes disponibilidad en esas fechas? Si todo encaja, me gustaría confirmar la reserva.

Gracias.`;
    
    return `https://wa.me/34675210872?text=${encodeURIComponent(message)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!gdprConsent) {
      toast.error('Debes aceptar la política de privacidad para continuar.');
      return;
    }

    setIsSubmitting(true);

    try {
      const utm = getStoredUtm();
      const payload = {
        nombre: formData.nombre,
        telefono: formData.telefono,
        email: formData.email,
        zona: formData.zona,
        servicio: formData.servicio,
        fecha_inicio: formData.fechaInicio,
        fecha_fin: formData.fechaFin || null,
        tamano: formData.tamano,
        caracter: formData.caracter,
        tiene_medicacion: hasMedication,
        medicacion: hasMedication ? formData.medicacion : null,
        observaciones: formData.observaciones || null,
        gdpr_consent: gdprConsent,
        website: website, // honeypot
        utm_source: utm.utm_source || null,
        utm_medium: utm.utm_medium || null,
        utm_campaign: utm.utm_campaign || null,
        utm_term: utm.utm_term || null,
        utm_content: utm.utm_content || null,
      };

      await axios.post(`${BACKEND_URL}/api/contact`, payload);

      // Track successful submission with service (no PII)
      trackContactFormSubmit(formData.servicio);

      // Generate WhatsApp link and open it
      const whatsappUrl = generateWhatsAppMessage();
      setWhatsappLink(whatsappUrl);
      
      setIsSubmitted(true);
      toast.success('¡Solicitud guardada! Abriendo WhatsApp...');
      
      // Open WhatsApp in new tab after short delay
      setTimeout(() => {
        window.open(whatsappUrl, '_blank');
      }, 500);
      
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Error al enviar. Inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="formulario" className="py-20 bg-section">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            className="max-w-2xl mx-auto text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-white rounded-2xl shadow-lg p-12">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#25D366' }}>
                <MessageCircle className="w-10 h-10 text-white" />
              </div>
              <h3 className="heading-2 mb-4">Te estoy redirigiendo a WhatsApp para enviar tu solicitud</h3>
              <p className="body-medium text-secondary mb-8">
                Si no se abre automáticamente, haz clic aquí:
              </p>
              <div className="flex flex-col gap-4 items-center">
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick('form_success', formData.servicio)}
                  data-testid="whatsapp-btn-form-success"
                >
                  <Button className="rounded-full gap-2 px-8 py-6 text-base" style={{ backgroundColor: '#25D366', color: 'white' }}>
                    <MessageCircle className="w-5 h-5" />
                    Abrir WhatsApp
                  </Button>
                </a>
                <Button
                  onClick={() => {
                    setIsSubmitted(false);
                    setWhatsappLink('');
                  }}
                  variant="link"
                  className="text-secondary text-sm"
                >
                  Enviar otra solicitud
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="formulario" className="py-20 bg-section">
      <div className="container mx-auto px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Section header */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="heading-1 mb-4">Cuéntame sobre tu perro</h2>
            <p className="body-medium text-secondary">
              Rellena el formulario y me pondré en contacto contigo para confirmar disponibilidad.
            </p>
          </motion.div>

          {/* Form */}
          <motion.form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-lg p-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Honeypot anti-spam: oculto para humanos, visible para bots */}
            <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
              <label htmlFor="website">No rellenar</label>
              <input
                type="text"
                id="website"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Nombre */}
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre *</Label>
                <Input
                  id="nombre"
                  name="nombre"
                  placeholder="Tu nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  required
                  className="rounded-xl"
                />
              </div>

              {/* Teléfono */}
              <div className="space-y-2">
                <Label htmlFor="telefono">Teléfono *</Label>
                <Input
                  id="telefono"
                  name="telefono"
                  type="tel"
                  placeholder="+34 600 000 000"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  required
                  className="rounded-xl"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="rounded-xl"
                />
              </div>

              {/* Zona */}
              <div className="space-y-2">
                <Label htmlFor="zona">Zona *</Label>
                <Select onValueChange={(value) => handleSelectChange('zona', value)} required>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Selecciona tu zona" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceZones.map((zone) => (
                      <SelectItem key={zone} value={zone}>
                        {zone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Servicio */}
              <div className="space-y-2">
                <Label htmlFor="servicio">Servicio requerido *</Label>
                <Select onValueChange={(value) => handleSelectChange('servicio', value)} required>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Selecciona un servicio" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceOptions.map((service) => (
                      <SelectItem key={service} value={service}>
                        {service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tamaño */}
              <div className="space-y-2">
                <Label htmlFor="tamano">Tamaño del perro *</Label>
                <Select onValueChange={(value) => handleSelectChange('tamano', value)} required>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Selecciona tamaño" />
                  </SelectTrigger>
                  <SelectContent>
                    {dogSizes.map((size) => (
                      <SelectItem key={size} value={size}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Fecha inicio */}
              <div className="space-y-2">
                <Label htmlFor="fechaInicio">Fecha de inicio *</Label>
                <Input
                  id="fechaInicio"
                  name="fechaInicio"
                  type="date"
                  value={formData.fechaInicio}
                  onChange={handleInputChange}
                  required
                  className="rounded-xl"
                />
              </div>

              {/* Fecha fin */}
              <div className="space-y-2">
                <Label htmlFor="fechaFin">Fecha de fin</Label>
                <Input
                  id="fechaFin"
                  name="fechaFin"
                  type="date"
                  value={formData.fechaFin}
                  onChange={handleInputChange}
                  className="rounded-xl"
                />
              </div>

              {/* Carácter */}
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="caracter">Carácter del perro *</Label>
                <Select onValueChange={(value) => handleSelectChange('caracter', value)} required>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Selecciona carácter" />
                  </SelectTrigger>
                  <SelectContent>
                    {dogCharacters.map((character) => (
                      <SelectItem key={character} value={character}>
                        {character}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Medicación */}
              <div className="space-y-4 md:col-span-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="hasMedication">¿Necesita medicación?</Label>
                  <Switch
                    id="hasMedication"
                    checked={hasMedication}
                    onCheckedChange={setHasMedication}
                  />
                </div>
                {hasMedication && (
                  <Textarea
                    name="medicacion"
                    placeholder="Describe la medicación y las instrucciones..."
                    value={formData.medicacion}
                    onChange={handleInputChange}
                    className="rounded-xl"
                  />
                )}
              </div>

              {/* Observaciones */}
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="observaciones">Observaciones adicionales</Label>
                <Textarea
                  id="observaciones"
                  name="observaciones"
                  placeholder="Cuéntame cualquier cosa que deba saber sobre tu perro..."
                  value={formData.observaciones}
                  onChange={handleInputChange}
                  className="rounded-xl min-h-[100px]"
                />
              </div>

              {/* GDPR Consent */}
              <div className="md:col-span-2">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="gdpr"
                    checked={gdprConsent}
                    onCheckedChange={setGdprConsent}
                    className="mt-1"
                  />
                  <label htmlFor="gdpr" className="text-sm text-secondary cursor-pointer">
                    He leído y acepto la{' '}
                    <a href="#privacidad" className="text-primary underline">
                      Política de Privacidad
                    </a>{' '}
                    y consiento el tratamiento de mis datos para gestionar mi solicitud. *
                  </label>
                </div>
              </div>
            </div>

            {/* Submit button */}
            <div className="mt-8">
              <Button
                type="submit"
                className="btn-primary rounded-full w-full py-6 text-base gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Enviar solicitud
                  </>
                )}
              </Button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
